import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';

/**
 * Estado de un reporte paginado del server: los filtros de la última búsqueda,
 * la página y la consulta. Nació en el Reporte ABM Web y lo comparte el Reporte
 * de padrón (VEN-512): las dos pantallas buscan, paginan y resetean igual.
 *
 * `obtener` tiene que ser estable (una función de módulo), porque no entra en
 * la `queryKey`: la identidad de la consulta la dan `clave`, filtros y página.
 */
export function useReportePaginado<P, R>(
  clave: string,
  obtener: (filtros: P, page: number, pageSize: number) => Promise<{ items: R[]; total: number }>,
  pageSize: number,
) {
  const [filtros, setFiltros] = useState<P | null>(null);
  const [page, setPage] = useState(1);

  const query = useQuery({
    queryKey: [clave, filtros, page, pageSize],
    queryFn: () => obtener(filtros as P, page, pageSize),
    enabled: filtros !== null,
    // Al pasar de página, mantener la tabla anterior en pantalla en vez de
    // vaciarla y mostrar el skeleton.
    //
    // Pero SOLO al pasar de página: `(prev) => prev` a secas conserva la data
    // ante cualquier cambio de queryKey, así que al buscar con filtros nuevos
    // se seguían viendo las filas y el contador del filtro anterior, sin
    // skeleton ni ninguna señal — presentados como si fueran el resultado de la
    // búsqueda nueva. Se compara la identidad del objeto `filtros`, que es
    // nuevo en cada búsqueda y estable al paginar.
    placeholderData: (prev, prevQuery) => (prevQuery?.queryKey[1] === filtros ? prev : undefined),
  });

  const rows = query.data?.items ?? [];
  const total = query.data?.total ?? 0;
  const totalPaginas = Math.max(1, Math.ceil(total / pageSize));

  /** El total puede bajar sin que cambien los filtros (en el ABM el balde de
   *  Activos depende del día; en el de padrón, la gente avanza de estado). Sin
   *  esto, la página queda apuntando más allá del final y —como el paginador
   *  solo se muestra con más de una página— desaparece el control para volver. */
  if (page > totalPaginas && query.data) setPage(totalPaginas);

  /** Una búsqueda nueva siempre arranca en la página 1: si no, se pediría la
   *  página 5 de un filtro que quizás tiene una sola. */
  function buscar(params: P) {
    setPage(1);
    setFiltros(params);
  }

  return { filtros, page, setPage, query, rows, total, totalPaginas, buscar };
}
